import { MCPIntegrationType } from "./constants";
import {
  Card,
  CardContent,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Input,
  Switch,
  ScrollArea,
  Button,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { ArrowRightIcon, SearchIcon, SettingsIcon } from "@/assets/icons";
import { useMemo, useState } from "react";
import ViewConnections from "./ViewConnections";
import {
  CommonIntegrationInstance,
  DatamateIntegration,
  TeammateSelectedIntegration,
} from "./mcpSlice";
import useMCPContext from "@/pages/mcp/useMCPContext";
import { getIntegrationIcon } from "@/pages/teammate/utils";
import useIntegrations from "../teammate/useIntegrations";

interface IntegrationProps {
  integration: CommonIntegrationInstance & DatamateIntegration;
  isSelected: boolean;
  toggleIntegration: () => void;
  disableToggle?: boolean;
  showConfigureButton?: boolean;
}

const Integration = ({
  integration,
  isSelected,
  toggleIntegration,
  disableToggle,
  showConfigureButton = true,
}: IntegrationProps) => {
  const { teammate, updateSelectedIntegration } = useMCPContext();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { getIntegration } = useIntegrations();
  const integrationFromServer = getIntegration(integration.id);
  const tools = (integrationFromServer || integration).tools ?? [];

  const filteredTools = tools.filter(tool =>
    tool.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const selectedIntegration: TeammateSelectedIntegration | undefined = teammate?.integrations?.find(
    i => integration.id === i.id
  );
  const selectedKeys = useMemo(
    () => new Set(selectedIntegration?.tools.map(t => t.key)),
    [selectedIntegration?.tools]
  );

  const getToolKeys = (tool: { key: string; enable_all?: string[] | null }): string[] =>
    tool.enable_all ?? [tool.key];

  const isToolEnabled = (tool: { key: string; enable_all?: string[] | null }): boolean =>
    getToolKeys(tool).every(k => selectedKeys.has(k));

  const enabledCount = selectedIntegration ? tools.filter(tool => isToolEnabled(tool)).length : 0;

  const handleToolToggle = (
    tool: { key: string; enable_all?: string[] | null },
    checked: boolean
  ) => {
    if (!selectedIntegration || !teammate) return;

    const keysToToggle = getToolKeys(tool);
    const updatedTools = checked
      ? [
          ...selectedIntegration.tools,
          ...keysToToggle
            .filter(k => !selectedIntegration.tools.some(t => t.key === k))
            .map(k => ({ key: k })),
        ]
      : selectedIntegration.tools.filter(t => !keysToToggle.includes(t.key));

    updateSelectedIntegration({ ...selectedIntegration, tools: updatedTools });
  };
  const totalCount =
    integration.type === MCPIntegrationType.CODE || integration.type === MCPIntegrationType.API
      ? 1
      : (tools.length ?? 0);

  return (
    <>
      <Card
        key={integration.id}
        className={`
    al-w-[250px] al-cursor-pointer al-transition-all al-duration-200 !al-py-1 !al-px-1 al-bg-gradient-to-r al-from-blue-50 al-to-blue-100
    ${
      isSelected
        ? "al-border-primary al-bg-primary/5 hover:al-bg-primary/10"
        : "al-opacity-75 hover:al-bg-muted/5"
    }
  `}
        tabIndex={0}
        role="button"
        aria-label={`${integration.name} integration${isSelected ? ", selected" : ""}`}
        onClick={() => {
          if (showConfigureButton) {
            toggleIntegration();
          } else {
            setIsSheetOpen(true);
          }
        }}
      >
        <CardContent className="!al-p-2">
          <Stack justify="between" align="center">
            <Stack align="center">
              {integration.id && getIntegrationIcon(integration.id)}
              <div>
                <span className="al-font-medium al-text-sm">{integration.name}</span>
                {tools ? (
                  <div className="al-text-xs al-text-muted-foreground">
                    {enabledCount}/{totalCount} capabilities enabled
                  </div>
                ) : null}
              </div>
            </Stack>
            {showConfigureButton ? (
              <Button
                variant="ghost"
                size="icon"
                title="Configure integration"
                className="!al-h-auto !al-p-0 !al-w-auto"
                onClick={e => {
                  e.stopPropagation();
                  setIsSheetOpen(true);
                }}
              >
                <SettingsIcon className="al-w-4 al-h-4" />
              </Button>
            ) : (
              <ArrowRightIcon className="al-w-4 al-h-4" />
            )}
          </Stack>
        </CardContent>
      </Card>

      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent side="right" className="al-max-h-[100vh]">
          <SheetHeader>
            <SheetTitle>
              <Stack justify="between" align="center" className="al-gap-2">
                <Stack align="center">
                  {integration.id && getIntegrationIcon(integration.id)}
                  {integration.name}
                </Stack>
                {disableToggle ? null : (
                  <Switch
                    checked={isSelected}
                    onCheckedChange={() => {
                      toggleIntegration();
                    }}
                  />
                )}
              </Stack>
            </SheetTitle>
          </SheetHeader>
          <div className="al-mt-6">
            <Stack direction="column" className="al-gap-6">
              {/* Capabilities Section */}
              <div>
                <h3 className="al-font-medium al-mb-2">Capabilities</h3>
                <div className="al-relative">
                  <SearchIcon className="al-absolute al-left-2 al-top-2.5 al-h-4 al-w-4 al-text-muted-foreground" />
                  <Input
                    placeholder="Search your ai tools"
                    className="al-pl-8"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="al-mt-2 al-text-sm al-text-muted-foreground">
                  {enabledCount}/{totalCount} enabled
                </div>
                <ScrollArea className="al-max-h-[50vh] al-my-4 al-overflow-y-auto">
                  <div className="al-space-y-3 al-pr-4">
                    {filteredTools?.map(tool => (
                      <Stack key={tool.key} justify="between" align="center">
                        <span className="al-text-sm">{tool.name}</span>
                        <Switch
                          disabled={disableToggle}
                          checked={isToolEnabled(tool)}
                          onCheckedChange={(checked: boolean) => handleToolToggle(tool, checked)}
                        />
                      </Stack>
                    ))}
                  </div>
                </ScrollArea>
                {integration.type !== MCPIntegrationType.EXTENSION ? (
                  <ViewConnections integration={integration} />
                ) : null}
              </div>
            </Stack>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default Integration;
